import { Component, OnInit } from "@angular/core";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { ComprehensiveRequestsService } from "src/services/comprehensive-requests.service";
import { Location } from "@angular/common";
import { RateCard } from "src/models/rate-card";
import { debounceTime, take } from "rxjs/operators";
import { Subject } from "rxjs";
import { Parameter } from "src/models/parameter";

@Component({
  selector: "app-quote-history-details",
  templateUrl: "./quote-history-details.component.html",
  styleUrls: ["./quote-history-details.component.css"],
})
export class QuoteHistoryDetailsComponent implements OnInit {
  formDisabled: boolean = true;
  Object = Object;
  rateCard: RateCard; // Selected Rate Card from the dropdown
  selectedInsurerRateCard: RateCard;
  allRateCards: RateCard[] = [];
  selectedSubParametersMap: { [key: string]: Parameter } = {};
  private marketValueChanged: Subject<void> = new Subject<void>();
  private modelYearChanged: Subject<void> = new Subject<void>();
  isUpdating: boolean = false;

  constructor(
    private location: Location,
    private requestService: ComprehensiveRequestsService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar
  ) {
    const rateCardId = +this.route.snapshot.paramMap.get("id");
    const canEdit = this.route.snapshot.queryParams.canEdit;
    this.formDisabled = !canEdit;

    if (this.router.getCurrentNavigation()?.extras?.state) {
      this.rateCard = new RateCard(
        rateCardId,
        this.router.getCurrentNavigation()?.extras?.state?.rateCard
      );
      this.getRateCards(this.rateCard.insurerId);
    } else {
      this.requestService
        .getQuoteRequestByVehicleId(rateCardId)
        .subscribe((x) => {
          this.rateCard = x;
          this.getRateCards(x.insurerId);
        });
    }
    this.marketValueChanged.pipe(debounceTime(1000)).subscribe(() => {
      this.formatRateCardParams(this.rateCard);
      this.recalculateQuote();
    });

    this.modelYearChanged.pipe(debounceTime(1000)).subscribe(() => {
      this.formatRateCardParams(this.rateCard);
      this.recalculateQuote();
    });
  }

  ngOnInit(): void {}
  getRateCards(selectedInsurerId: string) {
    const body = {
      carMake: this.rateCard?.vehicle.carMake,
      carModel: this.rateCard?.vehicle.carModel,
      purposeOfLicense: this.rateCard?.vehicle.purposeOfLicense,
    };
    this.requestService
      .getQuotes(body)
      .pipe(take(1))
      .subscribe((rateCards: RateCard[]) => {
        this.allRateCards = rateCards;
        this.selectedInsurerRateCard = this.allRateCards.find(
          (card) => card.insurerId === selectedInsurerId
        );
        this.formatRateCardParams(this.rateCard);
      });
  }

  formatRateCardParams(rc: RateCard) {
    this.selectedSubParametersMap = {};
    const parametersMap = rc.parametersMap;

    Object.keys(parametersMap).forEach((key) => {
      const parameter = parametersMap[key];

      if (parameter.subParametersMap) {
        const filteredSubParams: { [key: string]: Parameter } = {};

        Object.entries(parameter.subParametersMap).forEach(
          ([subKey, subParam]: [string, Parameter]) => {
            if (subParam.isEnabled) {
              filteredSubParams[subKey] = subParam;
            }
          }
        );
        const subParamValues = Object.values(filteredSubParams);
        parameter.subParametersMap = filteredSubParams;
        const quoteSubParamsMap =
          this.rateCard?.parametersMap[key]?.subParametersMap;
        if (quoteSubParamsMap && subParamValues.length > 0) {
          for (const subKey in quoteSubParamsMap) {
            const quoteSubParam = quoteSubParamsMap[subKey];
            if (quoteSubParam.isSelected) {
              this.selectedSubParametersMap[key] = quoteSubParam;
            }
          }
        } else {
          if (subParamValues.length > 0) {
            if (!this.selectedSubParametersMap[key]) {
              this.selectedSubParametersMap[key] = subParamValues[0];
            }
          } else {
            this.selectedSubParametersMap[key] = {
              higherRange: 6,
              lowerRange: 0,
              isMandatory: false,
              name: "Included",
              arabicName: "مدفوع",
              valueForInsurer: 0,
              percentageValue: 0,
              translatedName: () => "Included",
              translatedDescription: () => "مدفوع",
              isSelected: true,
              isEnabled: true,
            };
          }
        }
      }
    });
    this.rateCard = { ...this.rateCard, parametersMap };
  }

  onInsurerChange(rateCard: RateCard): void {
    this.selectedInsurerRateCard = rateCard;
    this.loadCurrentRateCardFrom(rateCard);
    this.formatRateCardParams(this.rateCard);
    if (this.rateCard) {
      this.rateCard.saiyartiPercentage = this.rateCard.saiyartiPercentage;
      this.rateCard.insurerPercentage = this.rateCard.insurerPercentage;
      this.rateCard.minimumPremiumAmount = this.rateCard.minimumPremiumAmount;
      this.rateCard.saiyartiRecommended = this.rateCard.saiyartiRecommended;
      this.rateCard.insurer = this.rateCard.insurer;
      this.rateCard.insurerId = this.rateCard.insurerId;

      this.recalculateQuote();
    }
  }

  selectComprehensiveSubParameter(
    parameter: Parameter,
    subParameter: Parameter
  ) {
    var parameter = this.rateCard?.parametersMap[parameter.name];
    for (const subParameterKey in parameter.subParametersMap) {
      var subP = parameter.subParametersMap[subParameterKey];
      subP.isSelected = subParameter.name == subP.name;

      var quoteDetailsSubParameter =
        this.rateCard?.parametersMap[parameter.name]?.subParametersMap[
          subParameterKey
        ];
      if (quoteDetailsSubParameter) {
        quoteDetailsSubParameter.isSelected = subP.isSelected;
      }
    }

    subParameter.isSelected = true;
    this.selectedSubParametersMap[parameter.name] = subParameter;

    this.recalculateQuote();
  }

  recalculateQuote(): void {
    // Uncomment this to enable the recalculation of the quote when the user changes the parameters
    // this.quoteDetails.comprehensiveAmount = 0;
    // this.quoteDetails.amount = 0;
    // this.calculateComprehensive();
  }

  private calculateComprehensive() {
    const saiyartiPercentage = this.rateCard?.saiyartiPercentage;
    const insurerPercentage = this.rateCard?.insurerPercentage;
    const minimumPremiumAmount = this.rateCard?.minimumPremiumAmount;

    var fees = 0;
    for (let index = 0; index < this.rateCard?.fees.length; index++) {
      const fee = this.rateCard?.fees[index];
      fees += fee.amount;
    }

    var premium = 0;
    var comprehensivePercentage = saiyartiPercentage + insurerPercentage;
    for (const key in this.rateCard?.parametersMap) {
      var parameter = this.rateCard?.parametersMap[key];

      if (parameter.name === "Waiver of Subrogation") {
        for (const key in parameter.subParametersMap) {
          if (this.selectedSubParametersMap[parameter.name]) {
            premium += +(
              this.selectedSubParametersMap[parameter.name]?.valueForInsurer ??
              0
            );
          } else {
            const subParameter = parameter.subParametersMap[key];
            if (subParameter?.isSelected) {
              premium += +(subParameter?.valueForInsurer ?? 0);
            }
          }
        }
      } else {
        if (this.selectedSubParametersMap[parameter.name]) {
          comprehensivePercentage -= +(
            this.selectedSubParametersMap[parameter.name]?.percentageValue ?? 0
          );
        } else {
          for (const key in parameter.subParametersMap) {
            const subParameter = parameter.subParametersMap[key];
            if (subParameter?.isSelected) {
              comprehensivePercentage -= +(subParameter?.percentageValue ?? 0);
            }
          }
        }
      }
    }

    premium +=
      (+comprehensivePercentage * this.rateCard?.vehicle.marketValue) / 100.0;
    if (premium < minimumPremiumAmount) {
      premium = minimumPremiumAmount;
    }
    if (this.rateCard) {
      this.rateCard.comprehensiveAmount += premium + fees;
      this.rateCard.comprehensiveAmount =
        +this.rateCard.comprehensiveAmount.toFixed(3);
      this.rateCard.amount = this.rateCard.comprehensiveAmount;
    }
  }
  onMarketValueChange() {
    this.marketValueChanged.next();
  }

  onModelYearChange() {
    this.modelYearChanged.next();
  }

  goBack(): void {
    this.location.back();
  }
  getFees(rateCard: RateCard): number {
    return rateCard.fees.reduce((acc, curr) => acc + curr.amount, 0);
  }

  updateCarDetails(formValue: any) {
    if (this.rateCard) {
      for (const key in this.rateCard.parametersMap) {
        const parameter = this.rateCard.parametersMap[key];
        parameter.subParametersMap = {
          [formValue[parameter.name]]: {
            higherRange: 6,
            lowerRange: 0,
            isMandatory: false,
            name: formValue[parameter.name],
            arabicName: formValue[parameter.name],
            valueForInsurer: 15,
            percentageValue: 0,
            translatedName: () => formValue[parameter.name],
            translatedDescription: () => formValue[parameter.name],
            isSelected: true,
            isEnabled: true,
          },
        };
      }
      this.rateCard.amount = Number.parseFloat(formValue.amount);
      this.rateCard.comprehensiveAmount = this.rateCard.amount;
      Object.assign(this.rateCard.user, { firstName: formValue.ownerName });
      Object.assign(this.rateCard.vehicle, {
        carMake: formValue.carMake,
        carModel: formValue.carModel,
        variant: formValue.variant,
        modelYear: formValue.modelYear,
        marketValue: formValue.marketValue,
      });
    }
    this.isUpdating = true;

    this.requestService
      .updateRequestOfCustomer(this.rateCard?.rateCardId, this.rateCard)
      .subscribe(
        () => {
          this.isUpdating = false;
          this.snackBar.open("The quote is updated successfully.", "OK", {
            horizontalPosition: "center",
            duration: 5000,
          });
        },
        (err) => {
          this.isUpdating = false;
          this.snackBar.open(err, "CANCEL", {
            horizontalPosition: "right",
          });
        }
      );
  }
  sort(x: any, y: any) {
    const aStepId = x?.value?.order ?? 0;
    const bStepId = y?.value?.order ?? 0;
    return aStepId > bStepId ? 1 : bStepId > aStepId ? -1 : 0;
  }
  sendEmail() {
    this.isUpdating = true;
    this.requestService.sendEmail(this.rateCard?.rateCardId).subscribe(
      () => {
        this.isUpdating = false;
      },
      (err) => {
        this.isUpdating = false;
      }
    );
  }
  loadCurrentRateCardFrom(rateCard: RateCard) {
    this.rateCard.insurer = rateCard.insurer;
    this.rateCard.insurerId = rateCard.insurerId;
    const newRateCard = new RateCard(this.rateCard.rateCardId, this.rateCard);
    newRateCard.parametersMap = rateCard.parametersMap;

    for (const key in this.rateCard.parametersMap) {
      const parameter = this.rateCard.parametersMap[key];
      for (const subKey in parameter.subParametersMap) {
        const subParameter = parameter.subParametersMap[subKey];
        if (subParameter.isSelected) {
          newRateCard.parametersMap[parameter.name].subParametersMap[
            subKey
          ].isSelected = subParameter.isSelected;
        } else {
          newRateCard.parametersMap[parameter.name].subParametersMap[
            subKey
          ].isSelected = false;
        }
      }
    }
    this.rateCard = newRateCard;
  }
}
